import { Injectable } from '@angular/core';

/**
 * Session cache for IT Labor Rate Dashboard responses, keyed on the exact request.
 *
 * Root-provided so it outlives the page: leaving the dashboard and coming back, or
 * stepping back to an earlier filter combination, is answered instantly. The data only
 * changes when the rate cards are regenerated, which is rare and announced, so a session
 * lifetime is safe; a full page reload clears it.
 *
 * Bounded so a long session of filter-flicking cannot grow without limit. Map keeps
 * insertion order, so the oldest entry is the first key.
 */
@Injectable({
  providedIn: 'root'
})
export class RateDashboardCacheService {

  /** Most responses held at once. A summary is a few KB, so this is a few MB at worst. */
  protected readonly ctMaxEntries = 80;

  protected readonly entries = new Map<string, unknown>();

  /** Gets a cached response, refreshing its position so it is evicted last. */
  public get<T>(key: string): T | undefined {
    if (!this.entries.has(key))
      return undefined;

    const value = this.entries.get(key) as T;
    this.entries.delete(key);
    this.entries.set(key, value);
    return value;
  }

  /** Stores a response, evicting the least recently used when full. */
  public set<T>(key: string, value: T): void {
    this.entries.delete(key);
    this.entries.set(key, value);

    while (this.entries.size > this.ctMaxEntries) {
      const oldest = this.entries.keys().next().value as string;
      this.entries.delete(oldest);
    }
  }

  /** Empties the cache - for when the data is known to have changed. */
  public clear(): void {
    this.entries.clear();
  }
}
